import { useMemo } from "react";
import { Link } from "react-router-dom";
import { Card, CardBody, CardHeader } from "reactstrap";
import { ColumnDef } from "@tanstack/react-table";
import TableContainer from "components/common/TableContainer";
import StatusBadge from "components/common/StatusBadge";
import { DateOnly } from "components/common/Date";
import { getLeaveApplications } from "features/Leaves/services/LeaveApplicationService";
import type { LeaveApplication } from "features/Leaves/types/leave-application.types";
import {
  LeaveApplicationStatus,
  leaveApplicationStatusMap,
} from "features/Leaves/constants/leave-application.constants";
import {
  leaveApplicationEmployeeLabel,
  leaveTypeLabel,
} from "features/Leaves/utils/leaveApplicationDisplay";

const REQUEST_PARAMS = { status: LeaveApplicationStatus.PENDING };

export default function PendingLeavesDashboardCard() {
  const columns: ColumnDef<LeaveApplication>[] = useMemo(() => [
    {
      header: "Employee",
      id: "employee",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <Link
          to={`/hr-management/leave-applications/${row.original.id}`}
          className="fw-medium link"
        >
          {leaveApplicationEmployeeLabel(row.original)}
        </Link>
      ),
    },
    {
      header: "Leave type",
      id: "leaveType",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <span className="text-muted small">{leaveTypeLabel(row.original)}</span>
      ),
    },
    {
      header: "Period",
      id: "period",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { fromDate, toDate } = row.original;
        const days =
          fromDate && toDate
            ? Math.round(
                (new Date(`${toDate}T12:00:00`).getTime() -
                  new Date(`${fromDate}T12:00:00`).getTime()) /
                  86_400_000,
              ) + 1
            : null;
        return (
          <div>
            <div className="small text-body">
              <DateOnly date={fromDate} />
              {" → "}
              <DateOnly date={toDate} />
            </div>
            {days != null ? (
              <span className="badge bg-light text-muted border">
                {days} {days === 1 ? "day" : "days"}
              </span>
            ) : null}
          </div>
        );
      },
    },
    {
      header: "Status",
      id: "status",
      enableColumnFilter: false,
      cell: ({ row }) => (
        <StatusBadge value={row.original.status} map={leaveApplicationStatusMap} />
      ),
    },
  ], []);

  return (
    <Card className="mb-4">
      <CardHeader className="d-flex align-items-center justify-content-between">
        <h5 className="card-title mb-0">Pending leave applications</h5>
        <Link
          to="/hr-management/leave-applications"
          className="btn btn-sm btn-soft-primary"
        >
          View all
        </Link>
      </CardHeader>
      <CardBody>
        <TableContainer
          columns={columns}
          fetchApi={getLeaveApplications}
          requestParams={REQUEST_PARAMS}
          syncPaginationToUrl={false}
          pageSize={10}
          globalSearch={false}
          hidePageSize
          divClass="table-responsive table-card mb-0"
          tableClass="table-nowrap align-middle mb-0"
          theadClass="table-light text-muted"
        />
      </CardBody>
    </Card>
  );
}
